import { ReactElement } from "react"
import { action, computed, makeObservable, observable, reaction } from "mobx"

import {
  TOUR_MODAL_PLACEMENTS,
  TOUR_STEP_STATUSES,
} from "@src/widgets/onboarding/types"
import { OnboardingStepComponentWrapProps } from "@src/widgets/onboarding/ui/tours/OnboardingStepComponentWrap"

export const enum STEP_MODAL_TYPES {
  DEFAULT,
  POINTER,
}

export type TourStepSettingsType = {
  highlightElements: string[]
  placement: TOUR_MODAL_PLACEMENTS
  type: STEP_MODAL_TYPES
}

const defaultSettings = (): TourStepSettingsType => {
  return {
    highlightElements: [],
    placement: TOUR_MODAL_PLACEMENTS.CENTER,
    type: STEP_MODAL_TYPES.DEFAULT,
  }
}

export abstract class TourStepBase {
  enable = true

  highlighted: HTMLElement[] = []

  settings!: TourStepSettingsType

  status: TOUR_STEP_STATUSES = TOUR_STEP_STATUSES.INIT

  constructor(settings: Partial<TourStepSettingsType> = {}) {
    makeObservable(this, {
      addHighlight: action.bound,
      cleanUp: action.bound,
      enable: observable,
      highlighted: observable,
      inProgress: computed,
      isClosed: computed,
      isCompleted: computed,
      removeHighLights: action.bound,
      runAction: action.bound,
      setComplete: action.bound,
      setEnable: action.bound,
      setStatus: action.bound,
      settings: observable,
      status: observable,
    })

    if (settings) {
      this.settings = {
        ...defaultSettings(),
        ...settings,
      }
    }
  }

  private intervalSearch() {
    let count = 0

    return (current: string) => {
      const intervalId = setInterval(() => {
        if (count > 10) {
          clearInterval(intervalId)
        }

        const element = document.querySelector(
          current as keyof HTMLElementTagNameMap,
        )

        if (element) {
          element.classList.add("onboard-highlight")
          this.highlighted.push(element)
          clearInterval(intervalId)
        }

        count++
      }, 300)
    }
  }

  addHighlight() {
    if (this.settings.highlightElements.length) {
      setTimeout(() => {
        this.highlighted = this.settings.highlightElements.reduce(
          (acc: HTMLElement[], current) => {
            const el = document.querySelector(
              current as keyof HTMLElementTagNameMap,
            )

            if (el) {
              el.classList.add("onboard-highlight")
              acc.push(el)
            } else {
              this.intervalSearch()(current)
            }

            return acc
          },
          [],
        )
      }, 100)
    }
  }

  cleanUp() {
    this.removeHighLights()
  }

  removeHighLights() {
    if (this.highlighted.length) {
      this.highlighted = this.highlighted.filter((el) => {
        el.classList.remove("onboard-highlight")

        return false
      })
    }
  }

  /**
   * Starts the step action and sets the step status to "in progress".
   *
   * @returns The result of the action.
   */
  async runAction() {
    this.setStatus(TOUR_STEP_STATUSES.IN_PROGRESS)

    return this.action().then(this.addHighlight)
  }

  setClosed() {
    this.cleanUp()

    this.setStatus(TOUR_STEP_STATUSES.CLOSED)
  }

  setComplete() {
    this.removeHighLights()

    this.setStatus(TOUR_STEP_STATUSES.COMPLETED)
  }

  setEnable(v: boolean) {
    this.enable = v
  }

  setStatus(status: TOUR_STEP_STATUSES) {
    this.status = status
  }

  get inProgress() {
    return this.status === TOUR_STEP_STATUSES.IN_PROGRESS
  }

  get isClosed() {
    return this.status === TOUR_STEP_STATUSES.CLOSED
  }

  get isCompleted() {
    return this.status === TOUR_STEP_STATUSES.COMPLETED
  }

  /**
   * Action to be executed when the step is being run.
   * Must be implemented by subclasses.
   */
  abstract action(): Promise<void>
  abstract component: () => ReactElement
  abstract name: string
  abstract popupProps(): Omit<OnboardingStepComponentWrapProps, "children">
}
